'use client';

import { trpc } from '@/app/api/trpc/client';
import { ThemeProvider } from '@/app/components/Theme';
import { ClerkProvider } from '@clerk/nextjs';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { httpBatchLink, splitLink } from '@trpc/client';
import { ReactNode, useState } from 'react';
import superjson from 'superjson';
import { SseListener } from '@/app/components/SseListener';
import { MultiSessionSupportProvider } from './MultiSessionSupportProvider';

// Procedures that are slow (heavy DB aggregation: analysis, weekly summaries,
// large getMany scans) get routed to their own httpBatchLink/batch window so
// they don't get bundled into the same HTTP request as fast procedures like
// settings/plan lookups. Without this, the calendar page's initial mount fires
// ~10+ queries in the same tick, tRPC's default batching lumps them into one
// request, and the whole response waits on the slowest procedure in the
// batch (Sentry showed p95 ~5s batches dominated by analysis/getWeeklySummary
// while settings/plan queries are individually fast). See NAO-133.
const SLOW_TRPC_PATH_PREFIXES = [
  'analysis.',
  'activities.getMany',
  'activities.getWeeklySummary',
  'workouts.getMany',
];

function isSlowTrpcOperation(path: string) {
  return SLOW_TRPC_PATH_PREFIXES.some((prefix) => path.startsWith(prefix));
}

export function Providers({ children }: { children: ReactNode }) {
  const [trpcClient] = useState(() =>
    trpc.createClient({
      links: [
        splitLink({
          condition: (op) => isSlowTrpcOperation(op.path),
          true: httpBatchLink({
            url: '/api/trpc',
            transformer: superjson,
            methodOverride: 'POST',
          }),
          false: httpBatchLink({
            url: '/api/trpc',
            transformer: superjson,
            methodOverride: 'POST',
          }),
        }),
      ],
    })
  );

  // Keep a stable QueryClient instance for the lifetime of the app.
  const [queryClient] = useState(() => new QueryClient());

  const publishableKey = process.env.NEXT_PUBLIC_CLERK_PUBLISHABLE_KEY;
  const fallbackRedirectUrl =
    process.env.NEXT_PUBLIC_CLERK_SIGN_IN_FALLBACK_REDIRECT_URL ?? '/calendar';
  const signInForceRedirectUrl =
    process.env.NEXT_PUBLIC_CLERK_SIGN_IN_FORCE_REDIRECT_URL ??
    '/user/callback';
  const signUpForceRedirectUrl =
    process.env.NEXT_PUBLIC_CLERK_SIGN_UP_FORCE_REDIRECT_URL ??
    signInForceRedirectUrl;
  const signUpFallbackRedirectUrl =
    process.env.NEXT_PUBLIC_CLERK_SIGN_UP_FALLBACK_REDIRECT_URL ??
    fallbackRedirectUrl;
  // Our dev/test Clerk instance is shared across localhost and preview
  // deployments. Clerk remembers a single "home origin" per browser for that
  // instance (whichever localhost you last used it from) and silently
  // redirects there instead of the current app unless the current origin is
  // explicitly allow-listed here.
  const allowedRedirectOrigins = process.env.NEXT_PUBLIC_BASE_URL
    ? [process.env.NEXT_PUBLIC_BASE_URL]
    : undefined;

  return (
    <ClerkProvider
      publishableKey={publishableKey}
      signUpUrl='/user/sign-up'
      signInUrl='/user/sign-in'
      waitlistUrl='/user/waitlist'
      afterMultiSessionSingleSignOutUrl='/user/sign-in'
      signInForceRedirectUrl={signInForceRedirectUrl}
      signUpForceRedirectUrl={signUpForceRedirectUrl}
      signInFallbackRedirectUrl={fallbackRedirectUrl}
      signUpFallbackRedirectUrl={signUpFallbackRedirectUrl}
      allowedRedirectOrigins={allowedRedirectOrigins}
    >
      <MultiSessionSupportProvider>
        <trpc.Provider client={trpcClient} queryClient={queryClient}>
          <QueryClientProvider client={queryClient}>
            <ThemeProvider>
              <SseListener />
              {children}
            </ThemeProvider>
          </QueryClientProvider>
        </trpc.Provider>
      </MultiSessionSupportProvider>
    </ClerkProvider>
  );
}
